import React from 'react';
import styled from 'styled-components';
import { Theme } from '../theme';
import { Prediction } from '../types/predictions';
import { formatPrediction } from '../utils/priceUtils';
import { formatDate } from '../utils/dateUtils';

const ControlsContainer = styled.div<{ theme: Theme }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const NavigationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const NavButton = styled.button<{ disabled?: boolean; theme: Theme }>`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.disabled ? props.theme.background : props.theme.surface};
  color: ${props => props.disabled ? props.theme.subText : props.theme.text};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.primary};
    color: white;
    border-color: ${props => props.theme.primary};
  }
  
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

const ProgressInfo = styled.div<{ theme: Theme }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 140px;
`;

const ProgressText = styled.span<{ theme: Theme }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
`;

const ProgressBar = styled.div<{ theme: Theme }>`
  width: 100%;
  height: 4px;
  background: ${props => props.theme.border};
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number; theme: Theme }>`
  height: 100%;
  width: ${props => props.progress}%;
  background: ${props => props.theme.primary};
  transition: width 0.3s ease;
`;

const CurrentPredictionInfo = styled.div<{ theme: Theme }>`
  padding: 12px;
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  text-align: center;
`;

const PredictionSummary = styled.div<{ theme: Theme }>`
  font-size: 13px;
  color: ${props => props.theme.text};
  line-height: 1.4;
`;

const QuoteText = styled.div<{ theme: Theme; expanded: boolean }>`
  font-size: 12px;
  color: ${props => props.theme.subText};
  line-height: 1.3;
  margin: 8px 0;
  padding: 8px;
  background: ${props => props.theme.background};
  border-radius: 6px;
  border: 1px solid ${props => props.theme.border};
  max-height: ${props => props.expanded ? 'none' : '60px'};
  overflow: hidden;
  position: relative;
`;

const ExpandButton = styled.button<{ theme: Theme }>`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  cursor: pointer;
  font-size: 11px;
  padding: 2px 4px;
  margin-top: 4px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const JumpControls = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

const JumpButton = styled(NavButton)`
  padding: 6px 10px;
  font-size: 12px;
  opacity: 0.8;
  
  &:hover:not(:disabled) {
    opacity: 1;
  }
`;

const KeyboardHints = styled.div<{ theme: Theme }>`
  text-align: center;
  font-size: 11px;
  color: ${props => props.theme.subText};
  opacity: 0.8;
  padding: 4px;
  border-top: 1px solid ${props => props.theme.border};
  margin-top: 8px;
`;

const KeyHint = styled.kbd`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 3px;
  padding: 1px 4px;
  font-size: 10px;
  margin: 0 2px;
`;

interface TreasureHuntControlsProps {
  currentIndex: number;
  totalPredictions: number;
  onNavigate: (index: number) => void;
  currentPrediction: Prediction | null;
  theme: Theme;
}

export const TreasureHuntControls: React.FC<TreasureHuntControlsProps> = ({
  currentIndex,
  totalPredictions,
  onNavigate,
  currentPrediction,
  theme
}) => {
  const [quoteExpanded, setQuoteExpanded] = React.useState(false);
  const progress = totalPredictions > 0 ? ((currentIndex + 1) / totalPredictions) * 100 : 0;
  
  // Reset quote expansion when prediction changes
  React.useEffect(() => {
    setQuoteExpanded(false);
  }, [currentIndex]);
  
  const handlePrevious = () => {
    if (currentIndex > 0) {
      onNavigate(currentIndex - 1);
    }
  };
  
  const handleNext = () => {
    if (currentIndex < totalPredictions - 1) {
      onNavigate(currentIndex + 1);
    }
  };
  
  const handleFirst = () => {
    onNavigate(0);
  };
  
  const handleLast = () => {
    onNavigate(totalPredictions - 1);
  };

  if (totalPredictions === 0) {
    return (
      <ControlsContainer theme={theme}>
        <ProgressInfo theme={theme}>
          <ProgressText theme={theme}>No predictions to explore</ProgressText>
        </ProgressInfo>
      </ControlsContainer>
    );
  }

  return (
    <ControlsContainer theme={theme}>
      {/* Main Navigation */}
      <NavigationRow>
        <NavButton
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          theme={theme}
          title="Previous prediction (←)"
        >
          ← Prev
        </NavButton>
        
        <ProgressInfo theme={theme}>
          <ProgressText theme={theme}>
            {currentIndex + 1} of {totalPredictions}
          </ProgressText>
          <ProgressBar theme={theme}>
            <ProgressFill progress={progress} theme={theme} />
          </ProgressBar>
        </ProgressInfo>
        
        <NavButton
          onClick={handleNext}
          disabled={currentIndex === totalPredictions - 1}
          theme={theme}
          title="Next prediction (→)"
        >
          Next →
        </NavButton>
      </NavigationRow>

      {/* Jump Controls */}
      <JumpControls>
        <JumpButton
          onClick={handleFirst}
          disabled={currentIndex === 0}
          theme={theme}
          title="Jump to first prediction"
        >
          ⏮ First
        </JumpButton>
        <JumpButton
          onClick={handleLast}
          disabled={currentIndex === totalPredictions - 1}
          theme={theme}
          title="Jump to last prediction"
        >
          Last ⏭
        </JumpButton>
      </JumpControls>

      {/* Current Prediction Summary */}
      {currentPrediction && (
        <CurrentPredictionInfo theme={theme}>
          <PredictionSummary theme={theme}>
            <strong>
              {currentPrediction.prediction_type === 'sentiment' && (
                <span style={{ 
                  marginRight: '6px', 
                  fontSize: '14px',
                  color: '#64B5F6' 
                }}>
                  💬
                </span>
              )}
              {currentPrediction.influencer?.name || 'Unknown'}
              {currentPrediction.prediction_type !== 'sentiment' && (
                <span style={{ 
                  marginLeft: '8px', 
                  fontSize: '11px', 
                  fontWeight: 'bold',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  backgroundColor: currentPrediction.success_status === 'success' ? 'rgba(39, 174, 96, 0.15)' : 
                                 currentPrediction.success_status === 'miss' ? 'rgba(192, 57, 43, 0.15)' : 
                                 'rgba(255, 152, 0, 0.15)',
                  color: currentPrediction.success_status === 'success' ? 'rgba(39, 174, 96, 1)' : 
                         currentPrediction.success_status === 'miss' ? 'rgba(192, 57, 43, 1)' : 
                         'rgba(255, 152, 0, 1)'
                }}>
                  {currentPrediction.success_status === 'success' ? 'SUCCESS' : 
                   currentPrediction.success_status === 'miss' ? 'MISS' : 
                   'PENDING'}
                </span>
              )}
            </strong>
            <br />
            {formatPrediction(currentPrediction)}
            
            {/* Show quote if it exists, with smart truncation */}
            {currentPrediction.quote && (
              <QuoteText theme={theme} expanded={quoteExpanded}>
                "{currentPrediction.quote}"
                {currentPrediction.quote.length > 200 && !quoteExpanded && (
                  <ExpandButton 
                    theme={theme} 
                    onClick={() => setQuoteExpanded(true)}
                  >
                    ...read more
                  </ExpandButton>
                )}
                {quoteExpanded && currentPrediction.quote.length > 200 && (
                  <ExpandButton 
                    theme={theme} 
                    onClick={() => setQuoteExpanded(false)}
                  >
                    show less
                  </ExpandButton>
                )}
              </QuoteText>
            )}
            
            <small>
              {currentPrediction.prediction_type === 'sentiment' ? (
                // For sentiment tweets, only show when it was made
                currentPrediction.prediction_made && (
                  <>📅 Made: {formatDate(currentPrediction.prediction_made, 'dd/MM/yyyy')}</>
                )
              ) : (
                // For price predictions, show target date and made date
                <>
                  {currentPrediction.prediction_type === 'target' ? '🎯 Target: ' : 
                   currentPrediction.prediction_type === 'top' ? '🔝 Top: ' :
                   currentPrediction.prediction_type === 'bottom' ? '📉 Bottom: ' :
                   currentPrediction.prediction_type === 'pricetimerange' ? '⏱️ Price by: ' :
                   '📊 Range: '}
                  {currentPrediction.target_time ? formatDate(currentPrediction.target_time, 'dd/MM/yyyy') : 
                   currentPrediction.end_date ? formatDate(currentPrediction.end_date, 'dd/MM/yyyy') : 
                   'Date not specified'}
                  {currentPrediction.prediction_made && (
                    <>
                      <br />
                      📅 Made: {formatDate(currentPrediction.prediction_made, 'dd/MM/yyyy')}
                    </>
                  )}
                </>
              )}
            </small>
          </PredictionSummary>
        </CurrentPredictionInfo>
      )}

      {/* Keyboard Shortcuts Hints */}
      <KeyboardHints theme={theme}>
        <KeyHint theme={theme}>←</KeyHint><KeyHint theme={theme}>→</KeyHint> Navigate • 
        <KeyHint theme={theme}>Home</KeyHint><KeyHint theme={theme}>End</KeyHint> Jump • 
        <KeyHint theme={theme}>Space</KeyHint><KeyHint theme={theme}>Esc</KeyHint> Exit
      </KeyboardHints>
    </ControlsContainer>
  );
};

export default TreasureHuntControls;